import type { AxiosResponse } from 'axios';
import type { LogisticsListParams, LogisticsListResponse } from './conversation';
import { getLogisticsV2 } from './logisticsV2';
import type {
  ErpShipment,
  GetLogisticsV2Query,
  LogisticsAttachment,
  LogisticsData,
  LogisticsDataSection,
  LogisticsNode,
  ReverseShipment,
  Shipment,
} from './logisticsV2';

/**
 * 物流 V2 -> 物流 V1 视图模型适配层。
 *
 * 调用方按 `logisticsList` / `lazadaLogisticsList` 的原始返回结构消费数据
 * （AxiosResponse，body 里是 { data, thirdTrackingInfo, reverseData }），
 * 这里把 V2 的运单 / ERP / 逆向 / 轨迹节点摊平回该结构，调用方无需改动。
 */

const toNumberValue = (value?: string | number | null) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
};

const toStringValue = (value?: string | number | null) =>
  value === undefined || value === null ? '' : String(value);

const splitIds = (value?: string) =>
  value
    ? String(value)
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

/** V1 trackingInfo.updateTime 是秒 */
const nodeTimeToSeconds = (node: LogisticsNode) =>
  String(Math.floor(toNumberValue(node.nodeTimeMs) / 1000));

const mapAttachments = (attachments?: LogisticsAttachment[]) =>
  (attachments ?? []).map((attachment) => ({
    filePath: toStringValue(attachment.filePath),
    fileName: toStringValue(attachment.fileName),
    url: toStringValue(attachment.url),
    type: (attachment.type ?? 'IMAGE') as 'IMAGE',
  }));

const mapNodes = (nodes?: LogisticsNode[]): Conversation.Trade.BaseTrackingInfo[] =>
  (nodes ?? []).map((node) => ({
    updateTime: nodeTimeToSeconds(node),
    description: toStringValue(node.description),
    descriptionTranslation: {} as Conversation.Trade.Translation,
    attachment: mapAttachments(node.attachments),
  }));

const toV1LeChatStatus = (status?: string): Conversation.Trade.LeChatLogisticsStatus =>
  (status ?? 'LE_CHAT_UNKNOWN_TRACE') as Conversation.Trade.LeChatLogisticsStatus;

const toV2Query = (params: LogisticsListParams): GetLogisticsV2Query => {
  const include: LogisticsDataSection[] = ['SHIPMENTS', 'ERP_SHIPMENTS', 'TRACKING_NODES'];

  if ('needReverse' in params && params.needReverse) {
    include.push('REVERSE_SHIPMENTS');
  }

  if ('internal_trade_ids' in params && params.internal_trade_ids) {
    return {
      internalTradeIds: splitIds(params.internal_trade_ids),
      include,
    };
  }

  return {
    internalStoreId: (params as { internalStoreId: number | string }).internalStoreId,
    externalTradeIds: splitIds((params as { externalTradeIds: string }).externalTradeIds),
    include,
  };
};

/** 平台正向运单 -> V1 Logistics[] */
const mapShipments = (data: LogisticsData): Conversation.Trade.Logistics[] => {
  const isMercado = data.platform === 'MERCADO';

  return (data.shipments ?? []).map((shipment: Shipment) => {
    // Mercado 的 V1 轨迹结构是 { updateTime: 毫秒, description: 日期类型 }，
    // 与其他平台「秒 + 任意描述」不同，这里按平台区分。
    const trackingInfo = isMercado
      ? (shipment.trackingNodes ?? []).map((node) => ({
          updateTime: toStringValue(node.nodeTimeMs),
          description: toStringValue(node.description),
        }))
      : mapNodes(shipment.trackingNodes);

    return {
      shippingCarrier: toStringValue(shipment.shippingCarrier),
      leChatStatus: toV1LeChatStatus(shipment.leChatTraceStatus),
      // V2 不再返回平台 LogisticsStatus 枚举
      trackingStatus: '' as Conversation.Trade.LogisticsStatus,
      internalTradeId: toStringValue(data.internalTradeId),
      externalTradeId: toStringValue(data.externalTradeId),
      trackingNumber: toStringValue(shipment.trackingNumber),
      firstMileTrackingNumber: shipment.firstMileTrackingNumber,
      lastMileTrackingNumber: shipment.lastMileTrackingNumber,
      plpNumber: shipment.plpNumber,
      packageNumber: toStringValue(shipment.packageId),
      trackingInfo: trackingInfo as Conversation.Trade.BaseTrackingInfo[],
      trackingClass: shipment.isBookingShipment ? 'BOOKING' : undefined,
      deliverySlaTimeMs: data.deliverySlaTimeMs ? toStringValue(data.deliverySlaTimeMs) : undefined,
      // Mercado 专属字段（V1 由物流接口直接返回，V2 在 Shipment 上）
      ...(isMercado
        ? {
            shipmentId: toStringValue(shipment.shipmentId),
            logisticsMode: toStringValue(shipment.logisticsMode),
            estimatedDeliveryLimitTimeMs: toStringValue(shipment.estimatedDeliveryLimitTimeMs),
          }
        : {}),
    } as Conversation.Trade.Logistics;
  });
};

/** ERP 运单 -> V1 ThirdLogistics[] */
const mapErpShipments = (data: LogisticsData): Conversation.Trade.ThirdLogistics[] =>
  (data.erpShipments ?? []).map((erp: ErpShipment) => ({
    externalTradeId: toStringValue(data.externalTradeId),
    internalTradeId: toStringValue(data.internalTradeId),
    gxbLogisticsNo: toStringValue(erp.gxbLogisticsNo),
    leChatStatus: toV1LeChatStatus(erp.leChatTraceStatus),
    logisticsNo: toStringValue(erp.logisticsNo),
    originStatus: toStringValue(erp.erpTraceStatusOriginal ?? erp.leChatTraceStatusOriginal),
    trackingNumber: toStringValue(erp.trackingNumber),
    shipmentId: '',
    trackingInfo: mapNodes(erp.trackingNodes),
    erpType: erp.erpType as Conversation.Trade.ThirdLogistics['erpType'],
    // [HISENSE] V2 把 extShipmentId 放在 logisticsNo 里
    extShipmentId: erp.erpType === 'HISENSE' ? erp.logisticsNo : undefined,
  }));

/** 逆向运单 -> V1 ReverseLogistics[] */
const mapReverseShipments = (data: LogisticsData): Conversation.Trade.ReverseLogistics[] =>
  (data.reverseShipments ?? []).map((reverse: ReverseShipment) => ({
    externalTradeId: toStringValue(data.externalTradeId),
    internalTradeId: toStringValue(data.internalTradeId),
    trackingInfo: mapNodes(reverse.trackingNodes),
    trackingNumber: toStringValue(reverse.trackingNumber),
    leChatStatus: toV1LeChatStatus(reverse.leChatTraceStatus),
    externalReturnId: toStringValue(reverse.externalReturnId),
    shippingCarrier: toStringValue(reverse.shippingCarrier),
    deliveryTimeMs: reverse.signedTimeMs ? toStringValue(reverse.signedTimeMs) : '',
    deliverySlaTimeMinMs: reverse.deliverySlaTimeMinMs
      ? toStringValue(reverse.deliverySlaTimeMinMs)
      : '',
    deliverySlaTimeMaxMs: reverse.deliverySlaTimeMaxMs
      ? toStringValue(reverse.deliverySlaTimeMaxMs)
      : '',
  }));

/** Lazada 单订单运单 -> V1 LazadaLogistics */
const mapLazadaLogistics = (data: LogisticsData): Conversation.Trade.LazadaLogistics => ({
  internalTradeId: toStringValue(data.internalTradeId),
  externalTradeId: toStringValue(data.externalTradeId),
  packages: (data.shipments ?? []).map((shipment: Shipment) => {
    const nodes = shipment.trackingNodes ?? [];

    return {
      shippingCarrier: toStringValue(shipment.shippingCarrier),
      leChatStatus: toV1LeChatStatus(shipment.leChatTraceStatus),
      trackingInfo: nodes.map((node) => ({
        updateTime: nodeTimeToSeconds(node),
        description: toStringValue(node.description),
        descriptionTranslation: {} as Conversation.Trade.Translation,
        attachment: mapAttachments(node.attachments),
        leChatStatus: toV1LeChatStatus(node.leChatTraceStatus),
        trackingStatusCode: toStringValue(node.leChatTraceStatusOriginal),
        trackingStatus: toStringValue(node.leChatTraceStatusOriginal),
        title: toStringValue(node.description),
      })),
      trackingNumber: toStringValue(shipment.trackingNumber),
      trackingStatus: toStringValue(shipment.leChatTraceStatusOriginal),
      trackingStatusCode: toStringValue(
        shipment.leChatTraceStatusOriginal,
      ) as Conversation.Trade.LazadaLogisticsPackage['trackingStatusCode'],
      latestTrackingTime: nodes[0] ? nodeTimeToSeconds(nodes[0]) : '',
      warehouseCode: toStringValue(shipment.warehouseCode),
    };
  }),
});

/** V1 logisticsList 等价实现 */
export function logisticsListV2AsV1(
  params: LogisticsListParams,
  signal?: Parameters<typeof getLogisticsV2>[1],
): Promise<AxiosResponse<LogisticsListResponse>> {
  return getLogisticsV2(toV2Query(params), signal).then((result) => {
    const logisticsData = result.logisticsData ?? [];

    return {
      data: {
        data: logisticsData.flatMap(mapShipments),
        thirdTrackingInfo: logisticsData.flatMap(mapErpShipments),
        reverseData: logisticsData.flatMap(mapReverseShipments),
      },
    } as AxiosResponse<LogisticsListResponse>;
  });
}

/** V1 lazadaLogisticsList 等价实现 */
export function lazadaLogisticsListV2AsV1(
  params: LogisticsListParams,
  signal?: Parameters<typeof getLogisticsV2>[1],
): Promise<
  AxiosResponse<{
    data: Conversation.Trade.LazadaLogistics[];
    thirdTrackingInfo?: Conversation.Trade.ThirdLogistics[];
  }>
> {
  return getLogisticsV2(toV2Query(params), signal).then((result) => {
    const logisticsData = result.logisticsData ?? [];

    return {
      data: {
        data: logisticsData.map(mapLazadaLogistics),
        thirdTrackingInfo: logisticsData.flatMap(mapErpShipments),
      },
    } as AxiosResponse<{
      data: Conversation.Trade.LazadaLogistics[];
      thirdTrackingInfo?: Conversation.Trade.ThirdLogistics[];
    }>;
  });
}
